"use client";

import React from "react";
import { type ReactNode } from "react";
import type { RowData, Table as TanstackTable } from "@tanstack/react-table";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "./pagination";

export type PaginationInfoProps<TData extends RowData = unknown> = {
	table: TanstackTable<TData>;
};

export function PaginationInfo<TData extends RowData>({
	table,
}: PaginationInfoProps<TData>): React.JSX.Element {
	const totalPageCount = table.getPageCount();
	const page = table.getState().pagination.pageIndex + 1;

	const renderPageNumbers = () => {
		const maxVisiblePages = 5;

		if (totalPageCount <= maxVisiblePages) {
			return Array.from({ length: totalPageCount }, (_, index) => {
				const pageNumber = index + 1;
				return (
					<PaginationItem key={pageNumber}>
						<PaginationLink
							href="#"
							isActive={page === pageNumber}
							onClick={(e) => {
								e.preventDefault();
								table.setPageIndex(pageNumber - 1);
							}}
						>
							{pageNumber}
						</PaginationLink>
					</PaginationItem>
				);
			});
		}

		const items: ReactNode[] = [];
		{
			items.push(
				<PaginationItem key={1}>
					<PaginationLink
						href="#"
						isActive={page === 1}
						onClick={(e) => {
							e.preventDefault();
							table.setPageIndex(0);
						}}
					>
						1
					</PaginationLink>
				</PaginationItem>,
			);

			if (page > 3) {
				items.push(
					<PaginationItem key="ellipsis-start">
						<PaginationEllipsis />
					</PaginationItem>,
				);
			}

			const start = Math.max(2, page - 1);
			const end = Math.min(totalPageCount - 1, page + 1);

			for (let i = start; i <= end; i += 1) {
				items.push(
					<PaginationItem key={i}>
						<PaginationLink
							href="#"
							isActive={page === i}
							onClick={(e) => {
								e.preventDefault();
								table.setPageIndex(i - 1);
							}}
						>
							{i}
						</PaginationLink>
					</PaginationItem>,
				);
			}

			if (page < totalPageCount - 2) {
				items.push(
					<PaginationItem key="ellipsis-end">
						<PaginationEllipsis />
					</PaginationItem>,
				);
			}

			items.push(
				<PaginationItem key={totalPageCount}>
					<PaginationLink
						href="#"
						isActive={page === totalPageCount}
						onClick={(e) => {
							e.preventDefault();
							table.setPageIndex(totalPageCount - 1);
						}}
					>
						{totalPageCount}
					</PaginationLink>
				</PaginationItem>,
			);
		}

		return items;
	};

	return (
		<div className="flex flex-col md:flex-row items-center gap-3 w-full">
			<Pagination>
				<PaginationContent className="max-sm:gap-0">
					<PaginationItem>
						<PaginationPrevious
							href="#"
							aria-disabled={!table.getCanPreviousPage()}
							tabIndex={!table.getCanPreviousPage() ? -1 : undefined}
							className={
								!table.getCanPreviousPage()
									? "pointer-events-none opacity-50"
									: undefined
							}
							onClick={(e) => {
								e.preventDefault();
								if (table.getCanPreviousPage()) {
									table.previousPage();
								}
							}}
						/>
					</PaginationItem>
					{renderPageNumbers()}
					<PaginationItem>
						<PaginationNext
							href="#"
							aria-disabled={!table.getCanNextPage()}
							tabIndex={!table.getCanNextPage() ? -1 : undefined}
							className={
								!table.getCanNextPage()
									? "pointer-events-none opacity-50"
									: undefined
							}
							onClick={(e) => {
								e.preventDefault();
								if (table.getCanNextPage()) {
									table.nextPage();
								}
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
		</div>
	);
}
